import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ShoppingBag, Plus, Minus, Check, Trash2, ShoppingCart, Sparkles, 
  Search, Filter, CheckCircle2, RefreshCw, Zap, Pencil, X, Save,
  Share2, Users, Heart, Home, ChevronRight, Lock, UserCheck, AlertCircle,
  Copy, Utensils, HeartPulse, CheckSquare, Calendar, Clock, Languages, Loader2,
  UserPlus, UserX, Bookmark, ShieldCheck, Contact
} from 'lucide-react';
import { 
  ShoppingItem, ShoppingList, CATEGORY_LABELS, QUICK_PRESETS,
  getCategoryLabel, getLocalizedPresetItem,
  getShoppingListsFromMemory, saveShoppingList, createShoppingList,
  renameShoppingList, deleteShoppingList, shareShoppingListWithEmail,
  unshareShoppingListEmail, subscribeToShoppingLists, syncShoppingDataFromCloud, isListOwner
} from '../lib/shoppingStorage';
import { 
  ApprovedContact, getApprovedContacts, addApprovedContact, 
  removeApprovedContact 
} from '../lib/contactsStorage';
import { toPersianDigits } from '../lib/storage';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import VoiceInputButton from './VoiceInputButton';
import { formatGregorianDate } from '../lib/dateUtils';
import { getStoredUser } from '../lib/api';
import { sendWorkerAiFullRequest } from '../lib/workerAi';

// Helper to adjust quantity text automatically (+1 / -1)
function adjustQuantityText(qty: string, delta: number): string {
  if (!qty) return delta > 0 ? `${toPersianDigits(1)} عدد` : '';
  const ascii = qty.replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
  const match = ascii.match(/([\d\.]+)/);
  if (!match) {
    return delta > 0 ? `${toPersianDigits(2)} ${qty}` : qty;
  }
  const currentNum = parseFloat(match[1]);
  if (isNaN(currentNum)) return qty;
  
  const step = currentNum >= 1 ? 1 : 0.5;
  const newNum = Math.max(0.5, currentNum + (delta * step));
  const formattedNewNum = toPersianDigits(Number.isInteger(newNum) ? newNum : newNum.toFixed(1));
  const origNumStr = qty.match(/[۰-۹\d\.]+/)?.[0] || match[1];
  return qty.replace(origNumStr, formattedNewNum);
}

// Preset list suggestions for quick notebook creation
const NOTEBOOK_PRESETS = [
  { name: 'خرید هفتگی خانه', key: 'shopPresetHome', color: 'emerald', icon: 'home' },
  { name: 'کباب و پیک‌نیک', key: 'shopPresetBBQ', color: 'rose', icon: 'utensils' },
  { name: 'داروخانه و بهداشتی', key: 'shopPresetPharmacy', color: 'indigo', icon: 'heart-pulse' },
  { name: 'خواربار و سوپرمارکت', key: 'shopPresetSupermarket', color: 'amber', icon: 'shopping-bag' },
  { name: 'خرید میوه و سبزی', key: 'shopPresetFruits', color: 'teal', icon: 'sparkles' }
];

export default function ShoppingModule() {
  const { lang, t } = useLanguage();
  const [lists, setLists] = useState<ShoppingList[]>(() => getShoppingListsFromMemory());
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Helper to format YYYY-MM-DD or ISO date string nicely in Gregorian
  const formatDisplayDate = (dateStr?: string): string => {
    if (!dateStr) return t('shopDateUnset', 'تعیین نشده');
    const todayStr = new Date().toISOString().split('T')[0];
    const rawDateStr = dateStr.split('T')[0];
    const formatted = formatGregorianDate(rawDateStr, lang, 'medium');
    if (rawDateStr === todayStr) {
      return t('shopTodayDateFmt', 'امروز ({{date}})', { date: formatted });
    }
    return formatted;
  };

  // New List Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListColor, setNewListColor] = useState('emerald');
  const [newListPurchaseDate, setNewListPurchaseDate] = useState<string>(() => new Date().toISOString().split('T')[0]);

  // Share Modal State
  const [shareModalList, setShareModalList] = useState<ShoppingList | null>(null);
  const [shareEmailInput, setShareEmailInput] = useState('');
  const [shareStatus, setShareStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [saveToApprovedOnShare, setSaveToApprovedOnShare] = useState(true);

  // Approved Contacts State
  const [approvedContacts, setApprovedContacts] = useState<ApprovedContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showContactsManager, setShowContactsManager] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactRelation, setNewContactRelation] = useState('خانواده');
  const [contactActionMsg, setContactActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [contactSearchQuery, setContactSearchQuery] = useState('');

  // Rename List Modal State
  const [renameModalList, setRenameModalList] = useState<ShoppingList | null>(null);
  const [renameInput, setRenameInput] = useState('');
  const [renamePurchaseDate, setRenamePurchaseDate] = useState('');

  // AI Translation State (Temporary Live Display - Zero Database Overwrite)
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationSuccessMsg, setTranslationSuccessMsg] = useState<string | null>(null);
  const [temporaryTranslations, setTemporaryTranslations] = useState<{
    lang: 'sv' | 'en' | 'fa';
    map: Record<string, string>;
  } | null>(null);
  const [copiedTranslations, setCopiedTranslations] = useState(false);

  // Inside Active List State
  const [inputTitle, setInputTitle] = useState('');
  const [inputQuantity, setInputQuantity] = useState('');
  const [inputCategory, setInputCategory] = useState<ShoppingItem['category']>('grocery');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [presetCategoryFilter, setPresetCategoryFilter] = useState<string>('all');

  // Preset Selection Modal State
  const [presetModal, setPresetModal] = useState<{
    title: string;
    category: ShoppingItem['category'];
    quantity: string;
  } | null>(null);

  // Inline editing state for items in list
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editQuantity, setEditQuantity] = useState('');
  const [editCategory, setEditCategory] = useState<ShoppingItem['category']>('grocery');

  const titleInputRef = useRef<HTMLInputElement>(null);
  const currentUser = getStoredUser();

  // Subscribe to real-time Cloudflare D1 sync across all devices
  useEffect(() => {
    setIsSyncing(true);
    const unsubscribe = subscribeToShoppingLists((updatedLists) => {
      setLists(updatedLists);
      setIsSyncing(false);
    });

    // Initial contacts load
    loadContacts();

    return () => unsubscribe();
  }, []);

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      const cList = await getApprovedContacts();
      setApprovedContacts(cList);
    } catch (e) {
      console.warn('Failed loading contacts:', e);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleManualSync = async () => {
    setIsSyncing(true);
    try {
      const refreshed = await syncShoppingDataFromCloud();
      setLists(refreshed);
      await loadContacts();
    } catch (e) {
      console.warn('Manual sync failed:', e);
    } finally {
      setIsSyncing(false);
    }
  };

  // Set default active list when lists load if none selected
  useEffect(() => {
    if (activeListId === null && lists.length > 0) {
      // Keep activeListId null initially so user sees the home overview, or let them pick
    }
  }, [lists, activeListId]);

  // Current Active List
  const activeList = useMemo(() => {
    if (!activeListId) return null;
    return lists.find(l => l.id === activeListId) || null;
  }, [lists, activeListId]);

  // Categorize Lists into "My Lists" and "Shared With Me"
  const { myLists, sharedWithMeLists } = useMemo(() => {
    const my: ShoppingList[] = [];
    const shared: ShoppingList[] = [];

    const userEmail = currentUser?.email?.toLowerCase();
    const userId = currentUser?.id;

    lists.forEach(list => {
      if (isListOwner(list, currentUser)) {
        my.push(list);
      } else if (
        (userEmail && list.sharedWithEmails && list.sharedWithEmails.some(e => e.toLowerCase() === userEmail)) ||
        (userId && list.sharedWithUids && list.sharedWithUids.includes(userId)) ||
        (!currentUser && list.ownerUid === 'guest_user')
      ) {
        shared.push(list);
      }
    });

    return { myLists: my, sharedWithMeLists: shared };
  }, [lists, currentUser]);

  // Handle List Item Updates
  const updateActiveListItems = (updater: ShoppingItem[] | ((prev: ShoppingItem[]) => ShoppingItem[])) => {
    if (!activeList) return;
    const nextItems = typeof updater === 'function' ? updater(activeList.items || []) : updater;
    const updatedList = { ...activeList, items: nextItems };
    
    // Update local state immediately
    setLists(prev => prev.map(l => l.id === activeList.id ? updatedList : l));
    saveShoppingList(updatedList);
  };

  const [isCreatingList, setIsCreatingList] = useState(false);

  // Create List
  const handleCreateList = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newListName.trim() || isCreatingList) return;

    setIsCreatingList(true);
    try {
      const created = await createShoppingList(newListName.trim(), newListColor, 'shopping-bag', newListPurchaseDate);
      setLists(prev => [created, ...prev.filter(l => l.id !== created.id)]);
      setNewListName('');
      setShowCreateModal(false);
      setActiveListId(created.id); // Open newly created list
    } finally {
      setIsCreatingList(false);
    }
  };

  // Quick Preset Create
  const handleQuickCreateList = async (preset: typeof NOTEBOOK_PRESETS[0]) => {
    if (isCreatingList) return;
    setIsCreatingList(true);
    try {
      const listTitle = t(preset.key, preset.name);
      const created = await createShoppingList(listTitle, preset.color, 'shopping-bag', new Date().toISOString().split('T')[0]);
      setLists(prev => [created, ...prev.filter(l => l.id !== created.id)]);
      setActiveListId(created.id);
    } finally {
      setIsCreatingList(false);
    }
  };

  // Rename & Update List Date
  const handleConfirmRenameList = async () => {
    if (!renameModalList || !renameInput.trim()) return;
    try {
      await renameShoppingList(renameModalList.id, renameInput.trim(), renamePurchaseDate);
      setLists(prev => prev.map(l => l.id === renameModalList.id ? { ...l, name: renameInput.trim(), purchaseDate: renamePurchaseDate || l.purchaseDate } : l));
      setRenameModalList(null);
    } catch (err: any) {
      alert(err?.message || t('error', 'خطا'));
    }
  };

  // Delete List
  const handleDeleteList = async (list: ShoppingList) => {
    const confirmText = t('shopConfirmDeleteList', 'آیا از حذف دفترچه «{{name}}» اطمینان دارید؟', { name: list.name });
    if (window.confirm ? window.confirm(confirmText) : true) {
      try {
        setLists(prev => prev.filter(l => l.id !== list.id));
        if (activeListId === list.id) {
          setActiveListId(null);
        }
        await deleteShoppingList(list.id);
      } catch (err: any) {
        alert(err?.message || t('error', 'خطا'));
      }
    }
  };

  // AI Translation of All Items in Active List (Temporary Live Display - DB Untouched)
  const handleTranslateList = async (targetLang: 'sv' | 'en' | 'fa') => {
    if (!activeList || !activeList.items || activeList.items.length === 0) {
      alert(t('shopNoItemsToTranslate', 'آیتمی برای ترجمه در این لیست وجود ندارد.'));
      return;
    }

    setIsTranslating(true);
    setTranslationSuccessMsg(null);

    const langNames = {
      sv: 'Swedish (Svenska)',
      en: 'English',
      fa: 'Persian (Farsi)'
    };

    const itemsToTranslate = activeList.items.map(i => ({ id: i.id, title: i.title }));
    const prompt = `You are an expert bilingual grocery and supermarket item translator.
Translate the following grocery item names into ${langNames[targetLang]}.
Keep the translations concise, natural, and standard for supermarket shopping in European and Swedish stores (e.g. ICA, Willys, Coop).
Target language code: ${targetLang}.

Input items JSON:
${JSON.stringify(itemsToTranslate)}

CRITICAL: Return ONLY a valid JSON array of objects with the exact schema:
[{"id": "string", "translatedTitle": "string"}]
Do NOT wrap in markdown formatting or explanations.`;

    try {
      const response = await sendWorkerAiFullRequest('translation', prompt);
      let translatedItems: Array<{ id: string; translatedTitle: string }> = [];

      if (response && response.reply) {
        const cleaned = response.reply.replace(/```json/gi, '').replace(/```/g, '').trim();
        const startIdx = cleaned.indexOf('[');
        const endIdx = cleaned.lastIndexOf(']');
        if (startIdx >= 0 && endIdx >= 0) {
          translatedItems = JSON.parse(cleaned.substring(startIdx, endIdx + 1));
        }
      }

      if (translatedItems && translatedItems.length > 0) {
        const transMapObj: Record<string, string> = {};
        translatedItems.forEach(t => {
          transMapObj[t.id] = t.translatedTitle;
        });

        // NOTE: Saved ONLY in temporary React state for live shopping display.
        // Database values are completely untouched!
        setTemporaryTranslations({
          lang: targetLang,
          map: transMapObj
        });
        
        const successMsg = t('shopTranslateSuccess', 'ترجمه موقت به زبان {{lang}} فعال شد! عناوین اصلی در دیتابیس بدون تغییر حفظ شدند.', { lang: langNames[targetLang] });
        setTranslationSuccessMsg(successMsg);
      } else {
        alert(t('shopTranslateFailed', 'پاسخ هوش مصنوعی قابل پردازش نبود. لطفاً مجدداً امتحان کنید.'));
      }
    } catch (err: any) {
      console.error('Translation error:', err);
      alert(err?.message || t('error', 'خطا در ارتباط با هوش مصنوعی'));
    } finally {
      setIsTranslating(false);
    }
  };

  const handleCopyTranslations = () => {
    if (!activeList || !activeList.items || !temporaryTranslations) return;
    const text = activeList.items.map(item => {
      const trans = temporaryTranslations.map[item.id];
      return trans ? `${item.title} ➡️ ${trans} (${item.quantity || '1'})` : `${item.title} (${item.quantity || '1'})`;
    }).join('\n');
    
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
    setCopiedTranslations(true);
    setTimeout(() => setCopiedTranslations(false), 2000);
  };

  // Share List Submit with Auto-Contact Save
  const handleShareSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailTrimmed = shareEmailInput.trim();
    if (!shareModalList || !emailTrimmed) return;

    setIsSharing(true);
    setShareStatus(null);

    const result = await shareShoppingListWithEmail(shareModalList.id, emailTrimmed);
    setIsSharing(false);

    if (result.success) {
      if (saveToApprovedOnShare) {
        addApprovedContact({
          name: emailTrimmed.split('@')[0],
          email: emailTrimmed,
          relation: 'خانواده'
        }).then(res => setApprovedContacts(res.contacts)).catch(() => {});
      }
      setShareStatus({ type: 'success', message: result.message });
      setShareEmailInput('');
    } else {
      setShareStatus({ type: 'error', message: result.message });
    }
  };

  // Quick 1-Click Share with Approved Contact or System User
  const handleQuickShareWithContact = async (email: string, contactName?: string) => {
    if (!shareModalList) return;
    setIsSharing(true);
    setShareStatus(null);

    const result = await shareShoppingListWithEmail(shareModalList.id, email);
    setIsSharing(false);

    if (result.success) {
      setShareStatus({
        type: 'success',
        message: t('shopSharedSuccessWithName', 'دفترچه با موفقیت با {{name}} ({{email}}) به اشتراک گذاشته شد.', { name: contactName || email, email })
      });
      setShareEmailInput('');
    } else {
      setShareStatus({ type: 'error', message: result.message });
    }
  };

  // Direct Contact Addition in Manager Modal
  const handleAddContactDirectly = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContactEmail.trim()) return;

    setLoadingContacts(true);
    setContactActionMsg(null);

    const res = await addApprovedContact({
      name: newContactName.trim() || newContactEmail.trim().split('@')[0],
      email: newContactEmail.trim(),
      relation: newContactRelation
    });

    setLoadingContacts(false);
    if (res.success) {
      setApprovedContacts(res.contacts);
      setContactActionMsg({ type: 'success', text: res.message || 'مخاطب اضافه شد.' });
      setNewContactName('');
      setNewContactEmail('');
    } else {
      setContactActionMsg({ type: 'error', text: res.message || 'خطا در افزودن مخاطب.' });
    }
  };

  // Direct Contact Removal
  const handleRemoveContact = async (id: string) => {
    const updated = await removeApprovedContact(id);
    setApprovedContacts(updated);
  };

  // Unshare Email
  const handleUnshareEmail = async (emailToUnshare: string) => {
    if (!shareModalList) return;
    try {
      await unshareShoppingListEmail(shareModalList.id, emailToUnshare);
      setShareStatus({ type: 'success', message: t('shopShareRevokedSuccess', 'اشتراک با {{email}} لغو شد.', { email: emailToUnshare }) });
    } catch (err: any) {
      setShareStatus({ type: 'error', message: err?.message || t('error', 'خطا') });
    }
  };

  // Item Management Inside Active List
  const handleAddItem = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const titleTrim = inputTitle.trim();
    if (!titleTrim || !activeList) return;

    const newItem: ShoppingItem = {
      id: `shop_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      title: titleTrim,
      quantity: inputQuantity.trim() || undefined,
      category: inputCategory,
      bought: false,
      createdAt: new Date().toISOString(),
      updatedByEmail: currentUser?.email || undefined
    };

    updateActiveListItems(prev => [newItem, ...prev]);
    setInputTitle('');
    setInputQuantity('');
  };

  const handleOpenPresetModal = (preset: { title: string; category: ShoppingItem['category']; quantity: string }) => {
    setPresetModal({
      title: preset.title,
      category: preset.category,
      quantity: preset.quantity || '۱ کیلو'
    });
  };

  const handleConfirmPresetAdd = () => {
    if (!presetModal || !activeList) return;
    const newItem: ShoppingItem = {
      id: `shop_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      title: presetModal.title,
      quantity: presetModal.quantity.trim() || undefined,
      category: presetModal.category,
      bought: false,
      createdAt: new Date().toISOString(),
      updatedByEmail: currentUser?.email || undefined
    };

    updateActiveListItems(prev => [newItem, ...prev]);
    setPresetModal(null);
  };

  const handleAdjustQuantity = (id: string, delta: number) => {
    updateActiveListItems(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = adjustQuantityText(item.quantity || '۱ کیلو', delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const handleStartEdit = (item: ShoppingItem) => {
    setEditingItemId(item.id);
    setEditTitle(item.title);
    setEditQuantity(item.quantity || '');
    setEditCategory(item.category);
  };

  const handleSaveEdit = (id: string) => {
    if (!editTitle.trim()) return;
    updateActiveListItems(prev => prev.map(item => {
      if (item.id === id) {
        return {
          ...item,
          title: editTitle.trim(),
          quantity: editQuantity.trim() || undefined,
          category: editCategory
        };
      }
      return item;
    }));
    setEditingItemId(null);
  };

  const handleToggleBought = (id: string) => {
    updateActiveListItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, bought: !item.bought };
      }
      return item;
    }));
  };

  const handleDeleteItem = (id: string) => {
    updateActiveListItems(prev => prev.filter(item => item.id !== id));
  };

  const handleClearBought = () => {
    if (window.confirm ? window.confirm(t('shopConfirmClearBought', 'آیا مطمئن هستید می‌خواهید تمام اقلام خریده‌شده را پاک کنید؟')) : true) {
      updateActiveListItems(prev => prev.filter(item => !item.bought));
    }
  };

  const handleClearAll = () => {
    if (window.confirm ? window.confirm(t('shopConfirmClearAll', 'آیا مطمئن هستید می‌خواهید کل این لیست را پاک کنید؟')) : true) {
      updateActiveListItems([]);
    }
  };

  // Filtered items inside active list
  const filteredItems = useMemo(() => {
    if (!activeList || !activeList.items) return [];
    return activeList.items.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.quantity && item.quantity.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [activeList, searchQuery, selectedCategory]);

  const activeItems = useMemo(() => filteredItems.filter(i => !i.bought), [filteredItems]);
  const boughtItems = useMemo(() => filteredItems.filter(i => i.bought), [filteredItems]);

  const filteredPresets = useMemo(() => {
    if (presetCategoryFilter === 'all') return QUICK_PRESETS;
    return QUICK_PRESETS.filter(p => p.category === presetCategoryFilter);
  }, [presetCategoryFilter]);

  // Is active list owned by current user
  const isOwnerOfActive = activeList ? isListOwner(activeList, currentUser) : false;

  return (
    <div className="space-y-6 pb-12 animate-fadeIn">
      {/* Top Banner & Header */}
      <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-indigo-700 text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold border border-white/30 flex items-center gap-1.5 backdrop-blur-xs">
                <ShoppingCart className="w-4 h-4 text-emerald-200" />
                {t('shoppingTitle', 'دفترچه خرید مشترک خانوادگی')}
              </span>
              <button
                onClick={handleManualSync}
                className="px-2.5 py-0.5 rounded-full bg-emerald-400/20 hover:bg-emerald-400/30 text-emerald-100 text-[11px] font-bold border border-emerald-300/30 flex items-center gap-1 transition-all cursor-pointer"
                title={t('refresh', 'بروزرسانی از دیتابیس D1')}
              >
                {isSyncing ? (
                  <>
                    <RefreshCw className="w-3 h-3 text-emerald-200 animate-spin" />
                    {t('loading', 'همگام‌سازی زنده...')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3 h-3 text-emerald-300" />
                    {t('synced', 'همگام با دیتابیس ابری')}
                  </>
                )}
              </button>
            </div>

            <h2 className="text-2xl sm:text-3xl font-extrabold font-display text-white flex items-center gap-2 flex-wrap">
              {activeList ? (
                <>
                  <button 
                    onClick={() => setActiveListId(null)}
                    className="p-1.5 rounded-xl hover:bg-white/20 text-white/90 transition-all cursor-pointer"
                    title={t('back', 'بازگشت به دفترچه‌ها')}
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <span>{activeList.name}</span>
                  <div className="flex items-center gap-2 mr-auto sm:mr-3">
                    <button
                      onClick={() => {
                        setRenameModalList(activeList);
                        setRenameInput(activeList.name);
                        setRenamePurchaseDate(activeList.purchaseDate || new Date().toISOString().split('T')[0]);
                      }}
                      className="px-3 py-1 rounded-xl bg-white/20 hover:bg-white/30 text-white text-xs font-bold transition-all border border-white/30 flex items-center gap-1.5 cursor-pointer"
                    >
                      <Calendar className="w-3.5 h-3.5 text-emerald-200" />
                      <span>{t('purchaseDateLabel', 'تاریخ خرید')}: {formatDisplayDate(activeList.purchaseDate)}</span>
                      <Pencil className="w-3 h-3 text-white/70" />
                    </button>
                  </div>
                </>
              ) : (
                t('shoppingHeaderTitle', 'مدیریت لیست خرید و دفترچه‌های هوشمند')
              )}
            </h2>
            <p className="text-xs sm:text-sm text-emerald-100 max-w-xl leading-relaxed">
              {activeList 
                ? t('shoppingHeaderDescActive', 'اقلام مورد نیاز را اضافه یا خریداری‌شده‌ها را علامت بزنید. تمام تغییرات به صورت زنده برای همسر یا اعضای خانواده بروزرسانی می‌شود.')
                : t('shoppingHeaderDescOverview', 'لیست‌های خرید خانه، کباب و سوپرمارکت را ایجاد کرده و با وارد کردن ایمیل همسر یا هم‌خانه‌ای، آن‌ها را به دفترچه مشترک تبدیل کنید.')}
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0 self-start md:self-center">
            {activeList ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveListId(null)}
                  className="px-4 py-2.5 rounded-2xl bg-white/20 hover:bg-white/30 backdrop-blur-md text-white text-xs font-bold transition-all border border-white/30 flex items-center gap-1.5 cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4" />
                  <span>{t('shopAllLists', 'همه دفترچه‌ها ({{count}})', { count: formatNumberByLang(lists.length, lang) })}</span>
                </button>
                {isOwnerOfActive && (
                  <button
                    onClick={() => {
                      setShareModalList(activeList);
                      setShareStatus(null);
                      setShareEmailInput('');
                    }}
                    className="px-4 py-2.5 rounded-2xl bg-emerald-400 text-slate-900 hover:bg-emerald-300 font-extrabold text-xs shadow-lg transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>{t('shopShareBtn', 'اشتراک‌گذاری')}</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => {
                    setShowContactsManager(true);
                    loadContacts();
                  }}
                  className="px-4 py-3 rounded-2xl bg-white/15 hover:bg-white/25 text-white font-extrabold text-xs sm:text-sm border border-white/25 shadow-md transition-all flex items-center gap-2 cursor-pointer backdrop-blur-md"
                >
                  <UserCheck className="w-4 h-4 text-emerald-200" />
                  <span>{t('manageContactsHeaderBtn', 'مخاطبین تاییدشده ({{count}})', { count: formatNumberByLang(approvedContacts.length, lang) })}</span>
                </button>

                <button
                  onClick={() => {
                    setNewListName('');
                    setShowCreateModal(true);
                  }}
                  className="px-5 py-3 rounded-2xl bg-white text-emerald-800 hover:bg-emerald-50 font-extrabold text-xs sm:text-sm shadow-lg transition-all flex items-center gap-2 cursor-pointer"
                >
                  <Plus className="w-5 h-5 text-emerald-600" />
                  <span>{t('shopNewListModalTitle', 'دفترچه خرید جدید')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RENDER MODE A: MAIN DASHBOARD (MY LISTS & SHARED WITH ME) */}
      {!activeList && (
        <div className="space-y-8 animate-fadeIn">
          {/* Quick Create Presets Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 space-y-3">
            <span className="text-xs font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
              {t('shopQuickThemeCreate', 'ساخت سریع دفترچه موضوعی:')}
            </span>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {NOTEBOOK_PRESETS.map((p, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickCreateList(p)}
                  className="px-3.5 py-2 rounded-2xl bg-gray-50 hover:bg-emerald-50 dark:bg-gray-900 dark:hover:bg-emerald-950/40 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:border-emerald-300 text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shrink-0"
                >
                  <Plus className="w-3.5 h-3.5 text-emerald-600" />
                  <span>{t(p.key, p.name)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Section 1: My Lists (لیست‌های من) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Home className="w-5 h-5 text-emerald-600" />
                <span>{t('shopMyLists', 'لیست‌های من')}</span>
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                  {toPersianDigits(myLists.length)}
                </span>
              </h3>

              <button
                onClick={() => setShowCreateModal(true)}
                className="text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>{t('shopAddNewList', 'افزودن دفترچه جدید')}</span>
              </button>
            </div>

            {myLists.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 text-center border border-gray-100 dark:border-gray-700 space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center mx-auto">
                  <ShoppingBag className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                  {t('shopNoListsYet', 'هنوز هیچ دفترچه خریدی ایجاد نکرده‌اید!')}
                </h4>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  {t('shopNoListsDesc', 'با کلیک روی «دفترچه خرید جدید»، اولین لیست خرید اختصاصی یا خانوادگی خود را بسازید.')}
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2.5 rounded-2xl bg-emerald-600 text-white font-bold text-xs shadow-md hover:bg-emerald-500 transition-all cursor-pointer inline-flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('shopCreateFirstBtn', 'ساخت دفترچه خرید جدید')}</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myLists.map(list => {
                  const activeCount = list.items ? list.items.filter(i => !i.bought).length : 0;
                  const boughtCount = list.items ? list.items.filter(i => i.bought).length : 0;
                  const isShared = list.sharedWithEmails && list.sharedWithEmails.length > 0;

                  return (
                    <div
                      key={list.id}
                      className="bg-white dark:bg-gray-800 rounded-3xl p-5 border border-gray-100 dark:border-gray-700/80 shadow-xs hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-600 transition-all flex flex-col justify-between space-y-4 group relative overflow-hidden"
                    >
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center text-emerald-600 shrink-0">
                              <ShoppingBag className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="text-base font-extrabold text-gray-900 dark:text-gray-100 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                                {list.name}
                              </h4>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-lg border border-emerald-200/50">
                                  {t('shopOwnerYou', '👑 مالک شما هستید')}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setRenameModalList(list);
                                setRenameInput(list.name);
                                setRenamePurchaseDate(list.purchaseDate || new Date().toISOString().split('T')[0]);
                              }}
                              className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
                              title={t('shopEditTitleDate', 'ویرایش عنوان و تاریخ خرید')}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteList(list)}
                              className="p-1.5 text-gray-400 hover:text-red-500 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
                              title={t('shopDeleteList', 'حذف دفترچه')}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Purchase Date Badge */}
                        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/80 px-3 py-2 rounded-2xl text-xs border border-gray-100 dark:border-gray-700/80">
                          <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5 text-[11px] font-medium">
                            <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                            {t('shopPurchaseDateLabel', 'تاریخ خرید / انجام:')}
                          </span>
                          <span className="font-extrabold text-emerald-700 dark:text-emerald-300 text-xs">
                            {formatDisplayDate(list.purchaseDate)}
                          </span>
                        </div>

                        {/* List Status Badges & Shared Info */}
                        <div className="flex items-center justify-between text-xs pt-1 border-t border-gray-100 dark:border-gray-700/60">
                          <div className="flex items-center gap-2 font-bold">
                            <span className="text-emerald-600 dark:text-emerald-400">
                              {t('shopRemainingItems', '{{count}} باقیمانده', { count: formatNumberByLang(activeCount, lang) })}
                            </span>
                            <span className="text-gray-300 dark:text-gray-600">•</span>
                            <span className="text-gray-400">
                              {t('shopBoughtItems', '{{count}} خریداری‌شده', { count: formatNumberByLang(boughtCount, lang) })}
                            </span>
                          </div>

                          {isShared ? (
                            <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-md flex items-center gap-1 border border-indigo-200 dark:border-indigo-800">
                              <Users className="w-3 h-3" />
                              {t('shopSharedWithCount', 'مشترک با {{count}} نفر', { count: formatNumberByLang(list.sharedWithEmails.length, lang) })}
                            </span>
                          ) : (
                            <span className="text-[10px] text-gray-400">{t('shopPersonalOnly', 'شخصی')}</span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 pt-2">
                        <button
                          onClick={() => setActiveListId(list.id)}
                          className="flex-1 py-2.5 px-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          <span>{t('shopOpenList', 'باز کردن دفترچه')}</span>
                        </button>

                        <button
                          onClick={() => {
                            setShareModalList(list);
                            setShareStatus(null);
                            setShareEmailInput('');
                          }}
                          className={`p-2.5 rounded-2xl border transition-all cursor-pointer ${
                            isShared
                              ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100'
                              : 'bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }`}
                          title={t('shopShareTooltip', 'اشتراک‌گذاری دفترچه با همسر یا هم‌خانه‌ای')}
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section 2: Shared With Me (به‌اشتراک‌گذاری‌شده با من) */}
          <div className="space-y-4 pt-4 border-t border-gray-200/60 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                <span>{t('shopSharedWithMe', 'به‌اشتراک‌گذاری‌شده با من')}</span>
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">
                  {toPersianDigits(sharedWithMeLists.length)}
                </span>
              </h3>
            </div>

            {sharedWithMeLists.length === 0 ? (
              <div className="bg-gradient-to-br from-indigo-50/50 via-purple-50/30 to-pink-50/30 dark:from-gray-800 dark:to-indigo-950/30 rounded-3xl p-8 border border-indigo-100 dark:border-indigo-900/50 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 flex items-center justify-center mx-auto shadow-inner">
                  <Heart className="w-6 h-6 fill-indigo-200 dark:fill-indigo-900" />
                </div>
                <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                  {t('shopNoSharedLists', 'هنوز دفترچه‌ای با شما به اشتراک گذاشته نشده است')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                  {t('shopNoSharedDesc', 'می‌توانید از همسر، اعضای خانواده یا هم‌خانه‌ای خود بخواهید با وارد کردن ایمیل شما در دکمه اشتراک‌گذاری، دفترچه خرید خود را با شما مشترک کنند.')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sharedWithMeLists.map(list => {
                  const activeCount = list.items ? list.items.filter(i => !i.bought).length : 0;
                  const boughtCount = list.items ? list.items.filter(i => i.bought).length : 0;

                  return (
                    <div
                      key={list.id}
                      className="bg-white dark:bg-gray-800 rounded-3xl p-5 border border-indigo-100 dark:border-indigo-900/50 shadow-xs hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700 transition-all flex flex-col justify-between space-y-4 group"
                    >
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-600 shrink-0">
                              <Heart className="w-5 h-5 fill-indigo-100 dark:fill-indigo-950" />
                            </div>
                            <div>
                              <h4 className="text-base font-extrabold text-gray-900 dark:text-gray-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                {list.name}
                              </h4>
                              <span className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded-md inline-block">
                                {t('shopCreatedBy', '🏠 ایجادکننده: {{name}}', { name: list.ownerName || list.ownerEmail })}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Status Bar */}
                        <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-100 dark:border-gray-700/60 font-bold">
                          <span className="text-emerald-600 dark:text-emerald-400">
                            {t('shopRemainingItems', '{{count}} باقیمانده', { count: formatNumberByLang(activeCount, lang) })}
                          </span>
                          <span className="text-gray-400">
                            {t('shopBoughtItems', '{{count}} خریداری‌شده', { count: formatNumberByLang(boughtCount, lang) })}
                          </span>
                        </div>
                      </div>

                      {/* Open List Button */}
                      <button
                        onClick={() => setActiveListId(list.id)}
                        className="w-full py-2.5 px-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <ShoppingBag className="w-4 h-4" />
                        <span>{t('shopEnterSharedList', 'ورود به دفترچه مشترک')}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* RENDER MODE B: INSIDE SELECTED LIST VIEW */}
      {activeList && (
        <div className="space-y-6 animate-fadeIn">
          {/* List Owner Header / Shared Info Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <span className={`text-xs font-bold px-3 py-1 rounded-xl flex items-center gap-1.5 ${
                isOwnerOfActive
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                  : 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800'
              }`}>
                {isOwnerOfActive ? t('shopYouAreOwner', '👑 شما مالک این لیست هستید') : t('shopSharedListFrom', '🏡 دفترچه مشترک ({{name}})', { name: activeList.ownerName || activeList.ownerEmail })}
              </span>

              {activeList.sharedWithEmails && activeList.sharedWithEmails.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 font-medium">
                  <Users className="w-3.5 h-3.5 text-indigo-500" />
                  {t('shopSharedWith', 'اشتراک با: {{emails}}', { emails: activeList.sharedWithEmails.join(', ') })}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 justify-end flex-wrap">
              {/* AI Translate List Button */}
              <button
                onClick={() => setShowTranslateModal(true)}
                className="px-3 py-1.5 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800 text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                title={t('shopAiTranslateListTooltip', 'ترجمه خودکار اقلام این لیست به سوئدی، انگلیسی یا فارسی با هوش مصنوعی')}
              >
                <Languages className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                <span>{t('shopAiTranslateList', 'ترجمه هوشمند اقلام (AI)')}</span>
              </button>

              {isOwnerOfActive ? (
                <>
                  <button
                    onClick={() => {
                      setShareModalList(activeList);
                      setShareStatus(null);
                      setShareEmailInput('');
                    }}
                    className="px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    <span>{t('shopManageShare', 'مدیریت اشتراک')}</span>
                  </button>

                  <button
                    onClick={() => {
                      setRenameModalList(activeList);
                      setRenameInput(activeList.name);
                    }}
                    className="p-1.5 rounded-xl text-gray-500 hover:text-emerald-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
                    title={t('shopRenameModalTitle', 'تغییر نام دفترچه')}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handleDeleteList(activeList)}
                    className="p-1.5 rounded-xl text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
                    title={t('shopDeleteList', 'حذف دفترچه')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <span className="text-[11px] text-gray-400 flex items-center gap-1" title={t('shopCollaboratorTooltip', 'تنها مالک لیست اجازه تغییر نام، حذف یا اشتراک‌گذاری دارد')}>
                  <Lock className="w-3 h-3" />
                  {t('shopCollaboratorAccess', 'دسترسی همکار')}
                </span>
              )}
            </div>
          </div>

          {/* Add Item Form */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <Plus className="w-4 h-4 text-emerald-500" />
              {t('shopAddNewItemTo', 'افزودن قلم جدید به «{{name}}»', { name: activeList.name })}
            </h3>

            <form onSubmit={handleAddItem} className="grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-5 flex items-center gap-2">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={inputTitle}
                  onChange={(e) => setInputTitle(e.target.value)}
                  placeholder={t('shopItemNamePh', 'نام کالا (مثلا: مرغ، کیوی، برنج...')}
                  className="flex-1 min-w-0 px-4 py-3 rounded-2xl text-xs sm:text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                />
                <VoiceInputButton
                  currentValue={inputTitle}
                  onUpdate={(val) => setInputTitle(val)}
                  title={t('voiceInputNormal', 'ورودی صوتی')}
                />
              </div>

              <div className="sm:col-span-3">
                <input
                  type="text"
                  value={inputQuantity}
                  onChange={(e) => setInputQuantity(e.target.value)}
                  placeholder={t('shopItemQtyPh', 'مقدار/وزن (مثلا: ۲ کیلو، ۱ بسته)')}
                  className="w-full px-4 py-3 rounded-2xl text-xs sm:text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                />
              </div>

              <div className="sm:col-span-2">
                <select
                  value={inputCategory}
                  onChange={(e) => setInputCategory(e.target.value as any)}
                  className="w-full px-3 py-3 rounded-2xl text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                >
                  {Object.entries(CATEGORY_LABELS).map(([catKey]) => (
                    <option key={catKey} value={catKey}>{getCategoryLabel(catKey as any, lang)}</option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={!inputTitle.trim()}
                  className="w-full py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('shopAddItemBtn', 'افزودن')}</span>
                </button>
              </div>
            </form>

            {/* Quick Presets Section */}
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                <span className="text-[11px] font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  {t('shopQuickSelectAdjust', 'انتخاب سریع و تنظیم وزن/تعداد:')}
                </span>

                {/* Quick preset category filters */}
                <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
                  <button
                    type="button"
                    onClick={() => setPresetCategoryFilter('all')}
                    className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap cursor-pointer ${
                      presetCategoryFilter === 'all'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t('shopAllPresets', 'همه ({{count}})', { count: formatNumberByLang(QUICK_PRESETS.length, lang) })}
                  </button>
                  {Object.entries(CATEGORY_LABELS).map(([catKey]) => {
                    const count = QUICK_PRESETS.filter(p => p.category === catKey).length;
                    if (count === 0) return null;
                    return (
                      <button
                        key={catKey}
                        type="button"
                        onClick={() => setPresetCategoryFilter(catKey)}
                        className={`px-2 py-0.5 rounded-lg text-[10px] font-medium transition-all whitespace-nowrap cursor-pointer ${
                          presetCategoryFilter === catKey
                            ? 'bg-emerald-600 text-white font-bold'
                            : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        {getCategoryLabel(catKey as any, lang)} ({formatNumberByLang(count, lang)})
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-1.5 overflow-x-auto pb-2 pt-1 flex-wrap max-h-48 overflow-y-auto custom-scrollbar">
                {filteredPresets.map((rawPreset, idx) => {
                  const preset = getLocalizedPresetItem(rawPreset, lang);
                  const isAlreadyAdded = activeList.items && activeList.items.some(i => i.title === preset.title && !i.bought);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleOpenPresetModal(preset)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                        isAlreadyAdded
                          ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 shadow-xs'
                          : 'bg-gray-50 hover:bg-emerald-50 dark:bg-gray-900 dark:hover:bg-emerald-950/30 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300'
                      }`}
                      title={t('shopClickToAdjust', 'برای تنظیم مقدار و افزودن کلیک کنید')}
                    >
                      <Plus className={`w-3.5 h-3.5 ${isAlreadyAdded ? 'text-emerald-600' : 'text-gray-400'}`} />
                      <span className="font-semibold">{preset.title}</span>
                      {preset.quantity && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">({preset.quantity})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Preset Quantity Modal / Popover */}
          {presetModal && (
            <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
              <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5 relative">
                <button
                  onClick={() => setPresetModal(null)}
                  className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="space-y-1">
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <ShoppingBag className="w-4 h-4" />
                    {t('shopAdjustQtyModalTitle', 'تنظیم مقدار و وزن کالا')}
                  </span>
                  <h3 className="text-xl font-extrabold text-gray-900 dark:text-gray-100">
                    {presetModal.title}
                  </h3>
                </div>

                <div className="space-y-3">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                    {t('shopQuickQtyPick', 'انتخاب سریع مقدار یا کیلو:')}
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {[
                      { label: t('shopQty1kg', '۱ کیلو'), val: '1 kg' },
                      { label: t('shopQty2kg', '۲ کیلو'), val: '2 kg' },
                      { label: t('shopQty3kg', '۳ کیلو'), val: '3 kg' },
                      { label: t('shopQty5kg', '۵ کیلو'), val: '5 kg' },
                      { label: t('shopQtyHalfKg', 'نیم کیلو'), val: '0.5 kg' },
                      { label: t('shopQty1pcs', '۱ عدد'), val: '1 pcs' },
                      { label: t('shopQty2pcs', '۲ عدد'), val: '2 pcs' },
                      { label: t('shopQty1pack', '۱ بسته'), val: '1 pack' },
                      { label: t('shopQty2pack', '۲ بسته'), val: '2 packs' }
                    ].map((qOpt) => (
                      <button
                        key={qOpt.val}
                        type="button"
                        onClick={() => setPresetModal(prev => prev ? { ...prev, quantity: qOpt.label } : null)}
                        className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                          presetModal.quantity === qOpt.label
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                            : 'bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                        }`}
                      >
                        {qOpt.label}
                      </button>
                    ))}
                  </div>

                  <div className="pt-2 space-y-1.5">
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                      {t('shopOrCustomQty', 'یا نوشتن مقدار دلخواه:')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={presetModal.quantity}
                        onChange={(e) => setPresetModal(prev => prev ? { ...prev, quantity: e.target.value } : null)}
                        placeholder={t('shopCustomQtyPh', 'مثلا: ۱.۵ کیلو یا ۴ بطری')}
                        className="flex-1 px-4 py-2.5 rounded-2xl text-xs sm:text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setPresetModal(prev => prev ? { ...prev, quantity: adjustQuantityText(prev.quantity, -1) } : null)}
                          className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 transition-all cursor-pointer"
                          title={t('shopDecrease', 'کاهش')}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPresetModal(prev => prev ? { ...prev, quantity: adjustQuantityText(prev.quantity, 1) } : null)}
                          className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 transition-all cursor-pointer"
                          title={t('shopIncrease', 'افزایش')}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-3">
                  <button
                    type="button"
                    onClick={handleConfirmPresetAdd}
                    className="flex-1 py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{t('shopAddToListNamed', 'افزودن به «{{name}}»', { name: activeList.name })}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPresetModal(null)}
                    className="py-3 px-4 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold hover:bg-gray-200 transition-all cursor-pointer"
                  >
                    {t('cancel', 'انصراف')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Category Filter & Clear Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === 'all'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {t('shopAllItemsCount', 'همه موارد ({{count}})', { count: formatNumberByLang(activeList.items?.length || 0, lang) })}
              </button>
              {Object.entries(CATEGORY_LABELS).map(([catKey]) => {
                const count = activeList.items ? activeList.items.filter(i => i.category === catKey).length : 0;
                if (count === 0 && selectedCategory !== catKey) return null;
                return (
                  <button
                    key={catKey}
                    onClick={() => setSelectedCategory(catKey)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === catKey
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {getCategoryLabel(catKey as any, lang)} ({formatNumberByLang(count, lang)})
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 justify-end">
              {boughtItems.length > 0 && (
                <button
                  onClick={handleClearBought}
                  className="px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:hover:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>{t('shopClearBought', 'پاک‌سازی خریده‌شده‌ها')}</span>
                </button>
              )}

              {activeList.items && activeList.items.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="px-2.5 py-1.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-950/30 dark:text-red-400 border border-red-200 dark:border-red-800 text-xs font-bold transition-all cursor-pointer"
                >
                  {t('shopClearAll', 'حذف همه')}
                </button>
              )}
            </div>
          </div>

          {/* Active Temporary Translation Banner */}
          {temporaryTranslations && (
            <div className="bg-purple-50/90 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800/70 rounded-2xl p-3.5 flex items-center justify-between gap-3 flex-wrap animate-fadeIn shadow-xs">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-purple-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
                  {temporaryTranslations.lang === 'sv' ? '🇸🇪' : temporaryTranslations.lang === 'en' ? '🇬🇧' : '🇮🇷'}
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs font-bold text-purple-900 dark:text-purple-200 flex items-center gap-1.5">
                    <span>
                      {t('shopActiveTranslationNotice', 'در حال نمایش ترجمه موقت به زبان {{lang}} (عناوین اصلی در دیتابیس محفوظ هستند)', {
                        lang: temporaryTranslations.lang === 'sv' ? 'سوئدی (Svenska)' : temporaryTranslations.lang === 'en' ? 'English' : 'فارسی'
                      })}
                    </span>
                  </div>
                  <p className="text-[11px] text-purple-700/80 dark:text-purple-400">
                    {t('shopTranslateNoDbHint', 'داده‌های اصلی پایگاه داده بدون تغییر باقی مانده و تنها برچسب‌های موقت خرید نمایش داده می‌شوند.')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowTranslateModal(true)}
                  className="px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all cursor-pointer flex items-center gap-1 shadow-xs"
                >
                  <Languages className="w-3.5 h-3.5" />
                  <span>{t('shopChangeTranslateLang', 'تغییر زبان')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTemporaryTranslations(null)}
                  className="px-3 py-1.5 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>{t('shopHideTranslation', 'پایان ترجمه موقت')}</span>
                </button>
              </div>
            </div>
          )}

          {/* Active Items Grid */}
          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-emerald-600" />
                {t('shopNeededItems', 'اقلام مورد نیاز برای خرید ({{count}})', { count: formatNumberByLang(activeItems.length, lang) })}
              </h3>

              {activeItems.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 text-center border border-gray-100 dark:border-gray-700 space-y-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center text-emerald-600 mx-auto">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {activeList.items?.length === 0 
                      ? t('shopListEmptyHint', 'این دفترچه خالی است! اقلام مورد نیاز را در کادر بالا یا انتخاب سریع وارد کنید.') 
                      : t('shopAllBoughtHint', 'تمام اقلام خریداری شده‌اند یا موردی در این دسته‌بندی یافت نشد.')}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {activeItems.map((item) => {
                    const isEditing = editingItemId === item.id;
                    const translatedTitle = temporaryTranslations?.map[item.id];

                    if (isEditing) {
                      return (
                        <div
                          key={item.id}
                          className="bg-emerald-50/70 dark:bg-emerald-950/30 p-4 rounded-2xl border-2 border-emerald-500 shadow-md space-y-3"
                        >
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              placeholder={t('item', 'نام کالا')}
                              className="w-full px-3 py-2 rounded-xl text-xs font-bold bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                            />
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editQuantity}
                                onChange={(e) => setEditQuantity(e.target.value)}
                                placeholder={t('quantity', 'مقدار/وزن')}
                                className="flex-1 px-3 py-2 rounded-xl text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                              />
                              <select
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value as any)}
                                className="px-2 py-2 rounded-xl text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                              >
                                {Object.entries(CATEGORY_LABELS).map(([ck]) => (
                                  <option key={ck} value={ck}>{getCategoryLabel(ck as any, lang)}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleSaveEdit(item.id)}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer shadow-xs"
                            >
                              <Save className="w-3.5 h-3.5" />
                              <span>{t('shopSave', 'ذخیره')}</span>
                            </button>
                            <button
                              onClick={() => setEditingItemId(null)}
                              className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-medium cursor-pointer"
                            >
                              {t('cancel', 'انصراف')}
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={item.id}
                        className={`bg-white dark:bg-gray-800 p-4 rounded-2xl border transition-all flex items-center justify-between gap-2 group ${
                          translatedTitle 
                            ? 'border-purple-200 dark:border-purple-800/80 shadow-xs hover:border-purple-400' 
                            : 'border-gray-100 dark:border-gray-700 shadow-xs hover:border-emerald-300 dark:hover:border-emerald-600'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                          <button
                            onClick={() => handleToggleBought(item.id)}
                            className="w-6 h-6 rounded-lg border-2 border-emerald-500/40 group-hover:border-emerald-600 flex items-center justify-center shrink-0 transition-colors cursor-pointer"
                            title={t('shopMarkBought', 'علامت زدن به عنوان خریداری‌شده')}
                          >
                            <div className="w-2.5 h-2.5 rounded-sm bg-transparent group-hover:bg-emerald-500/20" />
                          </button>

                          <div className="min-w-0 flex-1">
                            <div className="space-y-1">
                              <h4 className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">
                                {item.title}
                              </h4>
                              
                              {translatedTitle && (
                                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-purple-50 dark:bg-purple-950/60 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 text-xs font-bold shadow-2xs">
                                  <span>{temporaryTranslations?.lang === 'sv' ? '🇸🇪' : temporaryTranslations?.lang === 'en' ? '🇬🇧' : '🇮🇷'}</span>
                                  <span className="font-semibold">{translatedTitle}</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50">
                                <button
                                  onClick={() => handleAdjustQuantity(item.id, -1)}
                                  className="text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 p-0.5 rounded-md hover:bg-emerald-200/50 transition-all cursor-pointer"
                                  title={t('decrease', 'کاهش مقدار')}
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="text-[11px] font-mono font-bold text-emerald-800 dark:text-emerald-200 px-0.5">
                                  {item.quantity ? (lang === 'fa' ? toPersianDigits(item.quantity) : item.quantity) : t('shopOneKg', '۱ کیلو')}
                                </span>
                                <button
                                  onClick={() => handleAdjustQuantity(item.id, 1)}
                                  className="text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 p-0.5 rounded-md hover:bg-emerald-200/50 transition-all cursor-pointer"
                                  title={t('increase', 'افزایش مقدار')}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>

                              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {item.category === 'produce' ? t('shopCatProduce', 'میوه و سبزیجات') : item.category === 'dairy' ? t('shopCatDairy', 'لبنیات و پروتئین') : item.category === 'grocery' ? t('shopCatGrocery', 'خواربار و سوپرمارکت') : item.category === 'meat' ? t('shopCatMeat', 'گوشت و مرغ') : item.category === 'household' ? t('shopCatHousehold', 'لوازم خانه و بهداشتی') : t('shopCatOther', 'متفرقه')}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => handleStartEdit(item)}
                            className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-all cursor-pointer"
                            title={t('shopEditItem', 'ویرایش')}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-all cursor-pointer"
                            title={t('shopDeleteItem', 'حذف')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bought Items Section */}
            {boughtItems.length > 0 && (
              <div className="space-y-3 pt-4 border-t border-gray-200/60 dark:border-gray-800">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  {t('shopPurchasedSection', 'اقلام خریداری‌شده ({{count}})', { count: formatNumberByLang(boughtItems.length, lang) })}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {boughtItems.map((item) => {
                    const translatedTitle = temporaryTranslations?.map[item.id];
                    return (
                      <div
                        key={item.id}
                        className="bg-gray-50 dark:bg-gray-900/60 p-3.5 rounded-2xl border border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3 opacity-75 hover:opacity-100 transition-all"
                      >
                        <div 
                          onClick={() => handleToggleBought(item.id)}
                          className="flex items-center gap-3 cursor-pointer flex-1 min-w-0"
                        >
                          <div className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0">
                            <Check className="w-4 h-4 stroke-[3]" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 line-through truncate">
                                {item.title}
                              </h4>
                              {translatedTitle && (
                                <span className="text-xs font-semibold text-purple-600/70 dark:text-purple-400/70 line-through">
                                  ({translatedTitle})
                                </span>
                              )}
                            </div>
                            {item.quantity && (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {toPersianDigits(item.quantity)}
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-all cursor-pointer shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL 1: CREATE NEW LIST MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5 relative">
            <button
              onClick={() => setShowCreateModal(false)}
              className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                <ShoppingBag className="w-4 h-4" />
                {t('shopNewListModalTitle', 'دفترچه خرید جدید')}
              </span>
              <h3 className="text-xl font-extrabold text-gray-900 dark:text-gray-100">
                {t('shopNewListModalSubtitle', 'ایجاد دفترچه خرید شخصی یا خانوادگی')}
              </h3>
            </div>

            <form onSubmit={handleCreateList} className="space-y-4">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                    {t('shopListNameLabel', 'عنوان دفترچه:')}
                  </label>
                  <VoiceInputButton
                    currentValue={newListName}
                    onUpdate={(val) => setNewListName(val)}
                    title={t('voiceInputNormal', 'ورودی صوتی')}
                  />
                </div>
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder={t('shopListNamePh', 'مثلا: خرید کباب جمعه، داروخانه، سوپرمارکت...')}
                  className="w-full px-4 py-3 rounded-2xl text-xs sm:text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
              </div>

              {/* Purchase Date Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                  {t('shopListDateLabel', 'تاریخ خرید / برنامه‌ریزی:')}
                </label>
                <input
                  type="date"
                  value={newListPurchaseDate}
                  onChange={(e) => setNewListPurchaseDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-2xl text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Quick Presets */}
              <div className="space-y-1.5">
                <span className="text-xs font-bold text-gray-500">{t('shopQuickTitleSuggestions', 'پیشنهاد سریع عنوان:')}</span>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { name: 'خرید هفتگی خانه', key: 'shopPresetHome' },
                    { name: 'کباب و پیک‌نیک', key: 'shopPresetBBQ' },
                    { name: 'داروخانه و بهداشتی', key: 'shopPresetPharmacy' },
                    { name: 'خرید آنلاین', key: 'shopPresetOnline' }
                  ].map(preset => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => setNewListName(t(preset.key, preset.name))}
                      className="px-2.5 py-1 rounded-xl text-xs bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200 dark:border-gray-700 cursor-pointer"
                    >
                      {t(preset.key, preset.name)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-3">
                <button
                  type="submit"
                  disabled={!newListName.trim() || isCreatingList}
                  className="flex-1 py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-xs sm:text-sm shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>{isCreatingList ? t('shopCreating', 'در حال ایجاد...') : t('shopCreateListSubmit', 'ایجاد دفترچه')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="py-3 px-4 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold hover:bg-gray-200 transition-all cursor-pointer"
                >
                  {t('cancel', 'انصراف')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: SHARE LIST WITH EMAIL MODAL */}
      {shareModalList && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button
              onClick={() => {
                setShareModalList(null);
                setShareStatus(null);
              }}
              className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                <Heart className="w-4 h-4 fill-indigo-100" />
                {t('shopShareModalTitle', 'اشتراک‌گذاری خانوادگی دفترچه خرید')}
              </span>
              <h3 className="text-xl font-extrabold text-gray-900 dark:text-gray-100">
                {t('shopShareModalSubtitle', 'اشتراک‌گذاری «{{name}}»', { name: shareModalList.name })}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {t('shopShareModalDesc', 'می‌توانید با یک کلیک از لیست مخاطبین تاییدشده انتخاب کنید یا ایمیل جدید وارد نمایید.')}
              </p>
            </div>

            {/* SECTION A: QUICK SELECT FROM APPROVED CONTACTS */}
            <div className="space-y-2.5 p-3.5 rounded-2xl bg-indigo-50/50 dark:bg-gray-900/80 border border-indigo-100 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                  <UserCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  {t('approvedContactsQuickTitle', 'اشتراک سریع با مخاطبین تاییدشده:')}
                </span>
                <button
                  type="button"
                  onClick={() => setShowContactsManager(true)}
                  className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <Plus className="w-3 h-3" />
                  <span>{t('manageContactsBtn', 'مدیریت مخاطبین')}</span>
                </button>
              </div>

              {approvedContacts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto custom-scrollbar p-0.5">
                  {approvedContacts.map(contact => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => handleQuickShareWithContact(contact.email, contact.name)}
                      disabled={isSharing}
                      className="p-2.5 rounded-xl bg-white hover:bg-indigo-100/70 dark:bg-gray-800 dark:hover:bg-indigo-950/60 border border-indigo-100 dark:border-gray-700 text-right transition-all flex items-center justify-between group cursor-pointer shadow-2xs"
                    >
                      <div className="space-y-0.5 min-w-0 pr-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                            {contact.name}
                          </span>
                          {contact.relation && (
                            <span className="text-[9px] font-bold px-1.5 py-0.2 rounded-md bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300">
                              {contact.relation}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono block truncate" dir="ltr">
                          {contact.email}
                        </span>
                      </div>
                      <div className="p-1.5 rounded-lg bg-indigo-600 text-white opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all shrink-0">
                        <Share2 className="w-3.5 h-3.5" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-dashed border-indigo-200 dark:border-gray-700 text-center space-y-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('noContactsYetHint', 'هنوز مخاطب تاییدشده‌ای ثبت نکرده‌اید.')}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowContactsManager(true)}
                    className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>{t('addFirstContact', 'افزودن مخاطب جدید (همسر، برادر، همکار...)')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Share Form (Manual Input) */}
            <form onSubmit={handleShareSubmit} className="space-y-3 pt-1">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                  {t('shopRecipientEmail', 'یا وارد کردن دستی ایمیل مخاطب:')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={shareEmailInput}
                    onChange={(e) => setShareEmailInput(e.target.value)}
                    placeholder={t('emailPlaceholder', 'مثلا: example@mail.com')}
                    className="flex-1 px-4 py-3 rounded-2xl text-xs sm:text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    dir="ltr"
                  />
                  <button
                    type="submit"
                    disabled={isSharing || !shareEmailInput.trim()}
                    className="py-3 px-5 rounded-2xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer shrink-0"
                  >
                    {isSharing ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Share2 className="w-4 h-4" />
                    )}
                    <span>{t('shopSendShareBtn', 'ارسال اشتراک')}</span>
                  </button>
                </div>
                <label className="flex items-center gap-2 pt-1 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={saveToApprovedOnShare}
                    onChange={(e) => setSaveToApprovedOnShare(e.target.checked)}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 accent-indigo-600 cursor-pointer"
                  />
                  <span>{t('autoSaveToContactsCheck', 'حفظ خودکار این ایمیل در لیست مخاطبین تاییدشده من')}</span>
                </label>
              </div>
            </form>

            {/* Feedback Status Alert */}
            {shareStatus && (
              <div className={`p-4 rounded-2xl text-xs font-bold flex items-start gap-2.5 border animate-fadeIn ${
                shareStatus.type === 'success'
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700'
                  : 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700'
              }`}>
                {shareStatus.type === 'success' ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                )}
                <span className="leading-relaxed">{shareStatus.message}</span>
              </div>
            )}

            {/* List of currently shared emails */}
            {shareModalList.sharedWithEmails && shareModalList.sharedWithEmails.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <UserCheck className="w-4 h-4 text-indigo-500" />
                  {t('shopMembersWithAccess', 'اعضای دارای دسترسی به این دفترچه:')}
                </h4>
                <div className="space-y-1.5 max-h-36 overflow-y-auto custom-scrollbar">
                  {shareModalList.sharedWithEmails.map(sharedEmail => (
                    <div
                      key={sharedEmail}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-xs"
                    >
                      <span className="font-mono text-gray-800 dark:text-gray-200" dir="ltr">
                        {sharedEmail}
                      </span>
                      <button
                        onClick={() => handleUnshareEmail(sharedEmail)}
                        className="px-2 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-[10px] font-bold transition-all cursor-pointer"
                      >
                        {t('shopRevokeShare', 'لغو اشتراک')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-2 text-left">
              <button
                type="button"
                onClick={() => {
                  setShareModalList(null);
                  setShareStatus(null);
                }}
                className="py-2.5 px-4 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold hover:bg-gray-200 transition-all cursor-pointer"
              >
                {t('shopCloseModal', 'بستن')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: APPROVED CONTACTS MANAGER MODAL */}
      {showContactsManager && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-xl w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button
              onClick={() => {
                setShowContactsManager(false);
                setContactActionMsg(null);
              }}
              className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                <UserCheck className="w-4 h-4" />
                {t('contactsManagerSubtitle', 'لیست مخاطبین تاییدشده و اعضای مجاز')}
              </span>
              <h3 className="text-xl font-extrabold text-gray-900 dark:text-gray-100">
                {t('contactsManagerTitle', 'مدیریت مخاطبین اشتراک‌گذاری')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {t('contactsManagerDesc', 'اطلاعات همسر، هم‌خانه‌ای یا همکاران خود را ذخیره کنید تا هنگام اشتراک‌گذاری دفترچه‌های خرید، نیازی به تایپ مجدد ایمیل نباشد.')}
              </p>
            </div>

            {/* FORM: ADD NEW CONTACT */}
            <form onSubmit={handleAddContactDirectly} className="p-4 rounded-2xl bg-indigo-50/60 dark:bg-gray-900/70 border border-indigo-100 dark:border-gray-700 space-y-3">
              <h4 className="text-xs font-extrabold text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
                <UserPlus className="w-4 h-4 text-indigo-600" />
                <span>{t('addNewContactHeading', 'افزودن مخاطب جدید:')}</span>
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300 block">
                    {t('contactNameLabel', 'نام یا عنوان (مثلا: همسر - مریم):')}
                  </label>
                  <input
                    type="text"
                    value={newContactName}
                    onChange={(e) => setNewContactName(e.target.value)}
                    placeholder={t('contactNamePlaceholder', 'نام یا عنوان مخاطب')}
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300 block">
                    {t('contactEmailLabel', 'آدرس ایمیل:')}
                  </label>
                  <input
                    type="email"
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                    placeholder="example@mail.com"
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    dir="ltr"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1 space-y-1">
                  <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300 block">
                    {t('contactRelationLabel', 'نسبت/دسته‌بندی:')}
                  </label>
                  <select
                    value={newContactRelation}
                    onChange={(e) => setNewContactRelation(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="خانواده">{t('relationFamily', 'خانواده (همسر / فرزند / برادر...)')}</option>
                    <option value="دوست">{t('relationFriend', 'دوست / هم‌خانه‌ای')}</option>
                    <option value="همکار">{t('relationWork', 'همکار / کاری')}</option>
                    <option value="سایر">{t('relationOther', 'سایر')}</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loadingContacts || !newContactEmail.trim()}
                  className="mt-5 py-2.5 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer shrink-0"
                >
                  {loadingContacts ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  <span>{t('saveContactBtn', 'ذخیره مخاطب')}</span>
                </button>
              </div>
            </form>

            {contactActionMsg && (
              <div className={`p-3 rounded-xl text-xs font-bold ${
                contactActionMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'
              }`}>
                {contactActionMsg.text}
              </div>
            )}

            {/* LIST OF SAVED APPROVED CONTACTS */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                  <Bookmark className="w-4 h-4 text-indigo-500" />
                  <span>{t('savedContactsListHeader', 'مخاطبین تاییدشده ذخیره‌شده ({{count}}):', { count: formatNumberByLang(approvedContacts.length, lang) })}</span>
                </h4>
                {approvedContacts.length > 3 && (
                  <input
                    type="text"
                    value={contactSearchQuery}
                    onChange={(e) => setContactSearchQuery(e.target.value)}
                    placeholder={t('searchContactsPlaceholder', 'جستجوی مخاطب...')}
                    className="px-3 py-1 rounded-lg text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                  />
                )}
              </div>

              {approvedContacts.length === 0 ? (
                <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 text-center space-y-2">
                  <Contact className="w-8 h-8 text-gray-400 mx-auto" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('noContactsInList', 'هیچ مخاطب تاییدشده‌ای ثبت نشده است. از فرم بالا مخاطب جدید اضافه کنید.')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto custom-scrollbar">
                  {approvedContacts
                    .filter(c => !contactSearchQuery || c.name.toLowerCase().includes(contactSearchQuery.toLowerCase()) || c.email.toLowerCase().includes(contactSearchQuery.toLowerCase()))
                    .map(contact => (
                      <div
                        key={contact.id}
                        className="p-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 flex items-center justify-between"
                      >
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                              {contact.name}
                            </span>
                            {contact.relation && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">
                                {contact.relation}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono block" dir="ltr">
                            {contact.email}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveContact(contact.id)}
                          className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                          title={t('deleteContact', 'حذف مخاطب')}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="pt-2 text-left border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setShowContactsManager(false);
                  setContactActionMsg(null);
                }}
                className="py-2.5 px-5 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold hover:bg-gray-200 transition-all cursor-pointer"
              >
                {t('shopCloseModal', 'بستن')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: RENAME LIST MODAL */}
      {renameModalList && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-4 relative">
            <button
              onClick={() => setRenameModalList(null)}
              className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                <Pencil className="w-4 h-4" />
                {t('shopRenameModalTitle', 'تغییر نام دفترچه')}
              </span>
              <h3 className="text-lg font-extrabold text-gray-900 dark:text-gray-100">
                {t('shopRenameModalSubtitle', 'تغییر عنوان «{{name}}»', { name: renameModalList.name })}
              </h3>
            </div>

            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                  {t('shopNewListTitleLabel', 'عنوان جدید دفترچه:')}
                </label>
                <input
                  type="text"
                  value={renameInput}
                  onChange={(e) => setRenameInput(e.target.value)}
                  placeholder={t('shopNewListTitleLabel', 'عنوان جدید دفترچه')}
                  className="w-full px-4 py-3 rounded-2xl text-xs sm:text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                  {t('shopListDateLabel', 'تاریخ خرید / برنامه‌ریزی:')}
                </label>
                <input
                  type="date"
                  value={renamePurchaseDate}
                  onChange={(e) => setRenamePurchaseDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-2xl text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleConfirmRenameList}
                  disabled={!renameInput.trim()}
                  className="flex-1 py-2.5 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Save className="w-4 h-4" />
                  <span>{t('shopSaveRenameBtn', 'ذخیره تغییرات')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setRenameModalList(null)}
                  className="py-2.5 px-4 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold hover:bg-gray-200 transition-all cursor-pointer"
                >
                  {t('cancel', 'انصراف')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: AI TRANSLATE LIST MODAL (TEMPORARY DISPLAY ONLY) */}
      {showTranslateModal && activeList && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button
              onClick={() => {
                if (!isTranslating) {
                  setShowTranslateModal(false);
                  setTranslationSuccessMsg(null);
                }
              }}
              disabled={isTranslating}
              className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer disabled:opacity-30"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <span className="text-xs font-bold text-purple-600 dark:text-purple-400 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-purple-500" />
                {t('shopTranslateModalTitle', 'ترجمه موقت اقلام خرید (بدون تغییر دیتابیس)')}
              </span>
              <h3 className="text-lg font-extrabold text-gray-900 dark:text-gray-100">
                {t('shopTranslateModalSubtitle', 'ترجمه اقلام «{{name}}»', { name: activeList.name })}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('shopTranslateModalDesc', 'زبان مورد نظر برای مشاهده ترجمه اقلام در فروشگاه را انتخاب کنید. این ترجمه فقط برای نمایش بوده و عناوین اصلی ذخیره‌شده در پایگاه داده هرگز تغییر نخواهند کرد:')}
              </p>
            </div>

            {/* Translation in progress indicator */}
            {isTranslating && (
              <div className="p-4 rounded-2xl bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 flex items-center gap-3 animate-pulse">
                <Loader2 className="w-5 h-5 text-purple-600 dark:text-purple-400 animate-spin" />
                <span className="text-xs font-bold text-purple-800 dark:text-purple-300">
                  {t('shopTranslatingWait', 'در حال دریافت ترجمه اقلام با هوش مصنوعی... لطفاً چند لحظه صبر کنید')}
                </span>
              </div>
            )}

            {/* Success feedback */}
            {translationSuccessMsg && (
              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 flex items-center gap-3 text-emerald-800 dark:text-emerald-300 text-xs font-bold animate-fadeIn">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <span>{translationSuccessMsg}</span>
              </div>
            )}

            {/* Language Selection Buttons */}
            {!isTranslating && (
              <div className="space-y-2.5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => handleTranslateList('sv')}
                    className={`p-3 rounded-2xl border transition-all text-center flex flex-col items-center justify-center gap-1 group cursor-pointer ${
                      temporaryTranslations?.lang === 'sv'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 ring-2 ring-purple-400'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-purple-50/50'
                    }`}
                  >
                    <span className="text-xl">🇸🇪</span>
                    <span className="text-xs font-bold text-gray-900 dark:text-gray-100">Svenska</span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">ICA / Willys / Coop</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTranslateList('en')}
                    className={`p-3 rounded-2xl border transition-all text-center flex flex-col items-center justify-center gap-1 group cursor-pointer ${
                      temporaryTranslations?.lang === 'en'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 ring-2 ring-purple-400'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-purple-50/50'
                    }`}
                  >
                    <span className="text-xl">🇬🇧</span>
                    <span className="text-xs font-bold text-gray-900 dark:text-gray-100">English</span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">International</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTranslateList('fa')}
                    className={`p-3 rounded-2xl border transition-all text-center flex flex-col items-center justify-center gap-1 group cursor-pointer ${
                      temporaryTranslations?.lang === 'fa'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 ring-2 ring-purple-400'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-400 bg-gray-50 dark:bg-gray-900/60 hover:bg-purple-50/50'
                    }`}
                  >
                    <span className="text-xl">🇮🇷</span>
                    <span className="text-xs font-bold text-gray-900 dark:text-gray-100">فارسی</span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">اصلی</span>
                  </button>
                </div>

                {/* Translated Items Side-by-Side Dictionary Preview */}
                {temporaryTranslations && activeList.items && activeList.items.length > 0 && (
                  <div className="mt-4 space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                        <ShoppingBag className="w-3.5 h-3.5 text-purple-600" />
                        {t('shopTranslationsOverview', 'فهرست مقایسه‌ای اقلام و ترجمه فروشگاه')}
                      </span>
                      <button
                        type="button"
                        onClick={handleCopyTranslations}
                        className="text-[11px] font-bold text-purple-600 dark:text-purple-400 hover:text-purple-700 flex items-center gap-1 cursor-pointer"
                      >
                        {copiedTranslations ? (
                          <>
                            <Check className="w-3 h-3 text-emerald-600" />
                            <span className="text-emerald-600">{t('shopCopied', 'کپی شد!')}</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>{t('shopCopyTranslation', 'کپی ترجمه‌ها')}</span>
                          </>
                        )}
                      </button>
                    </div>

                    <div className="max-h-48 overflow-y-auto custom-scrollbar space-y-1.5 pr-1">
                      {activeList.items.map(item => {
                        const trans = temporaryTranslations.map[item.id];
                        return (
                          <div
                            key={item.id}
                            className="p-2 rounded-xl bg-gray-50 dark:bg-gray-900/60 border border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2 text-xs"
                          >
                            <div className="font-medium text-gray-700 dark:text-gray-300 truncate max-w-[45%]">
                              {item.title}
                              {item.quantity && <span className="text-[10px] text-gray-400 mr-1">({item.quantity})</span>}
                            </div>
                            <div className="font-bold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/60 px-2 py-0.5 rounded-lg border border-purple-200/60 dark:border-purple-800 truncate max-w-[50%]">
                              {trans || item.title}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              {temporaryTranslations && (
                <button
                  type="button"
                  onClick={() => setTemporaryTranslations(null)}
                  className="py-2 px-3 rounded-xl text-xs font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all cursor-pointer"
                >
                  {t('shopHideTranslation', 'پایان ترجمه موقت')}
                </button>
              )}
              
              <button
                type="button"
                disabled={isTranslating}
                onClick={() => {
                  setShowTranslateModal(false);
                  setTranslationSuccessMsg(null);
                }}
                className="py-2.5 px-5 rounded-2xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all cursor-pointer disabled:opacity-40 ml-auto shadow-xs"
              >
                {temporaryTranslations ? t('shopViewOnList', 'مشاهده روی لیست خرید') : t('close', 'بستن')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
